import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Star, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppFloat from '../components/WhatsAppFloat';
import GoogleReviews from '../components/GoogleReviews';
import FAQSection from '../components/FAQSection';
import { trackWhatsAppClick } from '../utils/analytics';

/**
 * Reusable SEO-focused landing page layout for dedicated service pages.
 * Used by: FilmagemFozPage, CriacaoDeSitesPage, LandingPagesPage.
 *
 * Each page passes its own copy + benefits + sections so that text content
 * is unique per URL (critical for Google ranking).
 */
const ServiceLandingPage = ({
  overline,
  h1,
  highlightWord,
  subtitle,
  whatsappMessage,
  heroImage,
  benefits,
  intro,
  blocks,
  faqLocal,
  gaSource,
  showLocalProof,
}) => {
  const handleCTA = () => {
    trackWhatsAppClick(gaSource, h1);
    window.open(
      `https://wa.me/5545988068508?text=${encodeURIComponent(whatsappMessage)}`,
      '_blank'
    );
  };

  return (
    <div className="App bg-[#0A0A0A] text-[#F5F5F0]">
      <Header />
      <main className="pt-20">
        {/* HERO */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          {/* Ambient backdrop */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(78,205,196,0.18)_0%,rgba(0,0,0,0)_55%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_70%,rgba(78,205,196,0.10)_0%,rgba(0,0,0,0)_50%)]" />
          </div>

          <div className="container mx-auto px-6 lg:px-12 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
              {/* Copy */}
              <div className="lg:col-span-7">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <Link
                    to="/"
                    className="inline-flex items-center text-xs uppercase tracking-[0.3em] text-[#A1A1AA] hover:text-[#4ECDC4] mb-4 transition-colors"
                  >
                    ← Voltar para o início
                  </Link>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-12 h-[1px] bg-[#4ECDC4]" />
                    <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#4ECDC4]">
                      {overline}
                    </span>
                  </div>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-bold text-[#F5F5F0] mb-6 leading-[1.05]">
                    {h1.split(highlightWord).map((part, i, arr) => (
                      <React.Fragment key={i}>
                        {part}
                        {i < arr.length - 1 && (
                          <span className="text-[#4ECDC4] italic font-light">
                            {highlightWord}
                          </span>
                        )}
                      </React.Fragment>
                    ))}
                  </h1>
                  <p className="text-base md:text-lg leading-relaxed text-[#A1A1AA] mb-8 font-light max-w-2xl">
                    {subtitle}
                  </p>

                  {/* Trust */}
                  {showLocalProof && (
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8 text-xs uppercase tracking-[0.25em] text-[#A1A1AA] font-light">
                      <span className="text-[#4ECDC4] font-bold flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 fill-[#4ECDC4]" /> 5,0 Google
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-[#4ECDC4]" /> Foz do Iguaçu, PR
                      </span>
                      <span>Desde 2015</span>
                    </div>
                  )}

                  {/* CTAs */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <motion.button
                      onClick={handleCTA}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      className="group bg-[#4ECDC4] hover:bg-[#F5F5F0] text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 shadow-xl shadow-[#4ECDC4]/30"
                      data-testid={`service-hero-cta-${gaSource}`}
                    >
                      <span>Solicitar Orçamento</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </motion.button>
                    <Link
                      to="/"
                      className="border border-[#4ECDC4]/50 text-[#F5F5F0] hover:bg-[#4ECDC4]/10 font-bold uppercase tracking-wide px-8 py-4 rounded-lg transition-all duration-300 flex items-center justify-center"
                    >
                      Ver outros serviços
                    </Link>
                  </div>
                </motion.div>
              </div>

              {/* Hero image */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="lg:col-span-5"
              >
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#4ECDC4]/30 shadow-[0_30px_80px_-20px_rgba(78,205,196,0.35)]">
                  <img
                    src={heroImage}
                    alt={h1}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-70" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* BENEFITS BAR */}
        <section className="py-16 border-t border-white/5 bg-[#0E0E0E]">
          <div className="container mx-auto px-6 lg:px-12 max-w-6xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {benefits.map((b, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="border border-white/10 rounded-xl p-5 bg-gradient-to-br from-[#161616] to-[#0E0E0E]"
                >
                  <div className="text-2xl md:text-3xl font-black text-[#4ECDC4] mb-1 leading-none">
                    {b.value}
                  </div>
                  <div className="text-[10px] md:text-xs text-[#A1A1AA] uppercase tracking-wider font-medium">
                    {b.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* INTRO LONG-FORM */}
        <section className="py-20 md:py-24">
          <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="prose prose-invert prose-lg max-w-none"
            >
              {intro.map((p, i) => (
                <p key={i} className="text-base md:text-lg text-[#D4D4D8] leading-relaxed mb-6 font-light">
                  {p}
                </p>
              ))}
            </motion.div>
          </div>
        </section>

        {/* CONTENT BLOCKS (H2 / list / etc.) */}
        {blocks.map((block, i) => (
          <section
            key={i}
            className={`py-16 md:py-20 ${i % 2 === 0 ? 'bg-[#0E0E0E]' : 'bg-[#0A0A0A]'} border-t border-white/5`}
          >
            <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
              <motion.h2
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-2xl md:text-4xl font-bold text-[#F5F5F0] mb-4 max-w-3xl leading-tight"
              >
                {block.h2}
              </motion.h2>
              {block.subtitle && (
                <p className="text-base text-[#A1A1AA] mb-10 max-w-2xl font-light leading-relaxed">
                  {block.subtitle}
                </p>
              )}

              {block.list && (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
                  {block.list.map((item, j) => (
                    <motion.li
                      key={j}
                      initial={{ opacity: 0, x: -16 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.4, delay: j * 0.05 }}
                      className="flex items-start gap-3 text-[#D4D4D8]"
                    >
                      <Check className="w-5 h-5 text-[#4ECDC4] flex-shrink-0 mt-0.5" />
                      <span className="font-light leading-relaxed">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              )}

              {block.paragraphs &&
                block.paragraphs.map((p, k) => (
                  <p key={k} className="text-base md:text-lg text-[#D4D4D8] leading-relaxed mb-6 font-light">
                    {p}
                  </p>
                ))}
            </div>
          </section>
        ))}

        {/* GOOGLE REVIEWS */}
        <GoogleReviews />

        {/* FAQ */}
        <FAQSection />

        {/* FINAL CTA */}
        <section className="py-20 md:py-28 border-t border-white/5 bg-gradient-to-b from-[#0A0A0A] to-[#0E0E0E]">
          <div className="container mx-auto px-6 lg:px-12 text-center max-w-3xl">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl font-bold text-[#F5F5F0] mb-6 leading-tight"
            >
              Pronto para começar?
            </motion.h2>
            <p className="text-base md:text-lg text-[#A1A1AA] mb-10 font-light max-w-xl mx-auto">
              Fale com nossa equipe agora pelo WhatsApp e receba um orçamento
              personalizado em até 2 horas úteis.
            </p>
            <motion.button
              onClick={handleCTA}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center space-x-2 bg-[#4ECDC4] hover:bg-[#F5F5F0] text-[#0A0A0A] font-bold uppercase tracking-wide px-10 py-5 rounded-lg transition-all duration-300 shadow-xl shadow-[#4ECDC4]/30"
              data-testid={`service-final-cta-${gaSource}`}
            >
              <span>Solicitar Orçamento Agora</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default ServiceLandingPage;
